import ChatWindowHeader from '../chat-window-header/ChatWindowHeader';
import useInfo from '../../hooks/useInfo';
import ProductivityGainModal from '../productivity-gain-modal/ProductivityGainModal';
import useMessagesState from '../../features/chat/hooks/useMessagesState';
import useScrollToBottom from '../../hooks/useScrollToBottom';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoute } from '../../router/Routing';
import { TestIds } from '../../mocks/ids';
import { Chat } from '../../features/chat';
import { ChatViewLayoutProps } from '../../layouts/chat-view-layout/ChatViewLayout';
import { useChat } from '../../contexts/chat/ChatContext';
import { useLayout } from '../../contexts/layout/LayoutContext';
import styles from './ChatWindow.module.scss';
import { ObsoleteNotice } from '../../ui/obsolete-notice/ObsoleteNotice';
import useLabels from '../../hooks/useLabels';
import { advancedAssistantKey, defaultPersonaKey } from '../../constants/consts';
import { useAdaptive } from '../../contexts/adaptive/AdaptiveContext';
import { Icons } from '../../ui/icon/icons/material';
import { IconTheme } from '../../ui/icon/Icon.types';

const ChatWindow: React.FC<ChatViewLayoutProps> = ({ chatId = '', onOpenSidebar, onReloadChats }) => {
    const navigate = useNavigate();
    const labels = useLabels();
    const infoProps = useInfo();
    const { catalogFavorites } = useAdaptive();
    const { prompt, createChat, persistent, updateChatMetadata, submitProductivityGain } = useChat();
    const { isRightSidebarVisible, isLeftSidebarVisible, expandLeftSidebar, collapseLeftSidebar, hideLeftSidebar, isEmbeddedMode } = useLayout();

    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollToBottomWithBuffer, scrollToBottomWithDelay } = useScrollToBottom(containerRef);

    const chatProps = useMessagesState({ chatId, infoProps, scrollToBottomWithDelay, prompt, createChat, persistent });
    const { chatHistoryError, isLoadingHistory, renderHistoryError, chat, modelMetadata } = chatProps.chat;

    const chatTitle = useMemo(() => chat?.session?.subject || '', [chat]);

    const [showProductivityModal, setShowProductivityModal] = useState(false);
    const onCloseProductivityModal = useCallback(() => setShowProductivityModal(false), []);
    const onOpenProductivityModal = useCallback(() => setShowProductivityModal(true), []);
    const onSubmitProductivityGain = useCallback(async (productivityGain: number) => {
        if (!chatId) return;
        await submitProductivityGain(chatId, productivityGain);
        setShowProductivityModal(false);
        await onReloadChats?.(true);
    }, [chatId, onReloadChats, submitProductivityGain]);

    const onRenameChatHandler = useCallback(async (newName: string) => {
        if (!chatId) return;
        const result = await updateChatMetadata(chatId, { subject: newName });
        await onReloadChats?.(true);
        return result;
    }, [chatId, onReloadChats, updateChatMetadata]);

    const onNavigateToNewChat = useCallback(() => {
        const route = infoProps.getPersonaFromKey(chat?.session.persona ?? "")?.route;
        navigate(route ? '/' + route : AppRoute.chat);
    }, [navigate, infoProps, chat]);

    const onResetSession = useCallback(() => {
        onNavigateToNewChat();
    }, [onNavigateToNewChat]);

    const chatConfig = useMemo(() => {
        if (!isEmbeddedMode) return undefined;
        return {
            input: {
                extraInputActions: [{
                    iconName: Icons.refresh,
                    theme: IconTheme.error,
                    title: labels.newSession,
                    onClick: onResetSession
                }]
            }
        };
    }, [isEmbeddedMode, labels.newSession, onResetSession]);

    const handleChatViewKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        // Ctrl/Cmd + Shift + K will start a new chat
        const holdsCtrlOrCmd = e.ctrlKey || e.metaKey;
        if (!holdsCtrlOrCmd) return;
        const holdsShift = e.shiftKey;
        if (!holdsShift) return;
        if (e.key.toLowerCase() !== 'k') return;

        e.preventDefault();
        e.stopPropagation();
        onNavigateToNewChat();
    }, [onNavigateToNewChat]);

    if (!!chatHistoryError) return renderHistoryError;

    return <Chat.ContextProvider value={chatProps} config={chatConfig} onReloadChats={onReloadChats}>
        <div data-testid={TestIds.chatWindow} className={styles['chat-window']} onKeyDown={handleChatViewKeyDown} tabIndex={0}>
            <div className={styles['chat-window-wrapper']}>
                <ChatWindowHeader
                    isRightSidebarVisible={isRightSidebarVisible}
                    isLeftSidebarVisible={isLeftSidebarVisible}
                    expandLeftSidebar={() => {
                        expandLeftSidebar();
                        // showLeftSidebar();
                    }}
                    collapseLeftSidebar={() => {
                        collapseLeftSidebar();
                        hideLeftSidebar();
                    }}
                    initialTitle={chatTitle}
                    onBurgerClick={onOpenSidebar}
                    onClockClick={onOpenProductivityModal}
                    onCreateNewChat={onNavigateToNewChat}
                    onRenameChat={onRenameChatHandler}
                    disableInteractions={isLoadingHistory}
                />
                {chat?.session?.persona === advancedAssistantKey && (
                    <div className={styles['notice-container']}>
                        <ObsoleteNotice
                            message={labels.advancedObsoleteDescription}
                            buttonLabel={labels.basicChatAssistantTitle}
                            onClick={() => {
                                catalogFavorites.swapPinnedPersonas?.(advancedAssistantKey, defaultPersonaKey);
                                navigate(AppRoute.basicAssistant);
                            }}
                        />
                    </div>
                )}
                <Chat.Messages ref={containerRef} scrollToBottomWithBuffer={scrollToBottomWithBuffer} />
            </div>

            <Chat.Input />

            <ProductivityGainModal initialProductivityGain={modelMetadata.productivityGain} chatId={chatId} isOpen={showProductivityModal} onClose={onCloseProductivityModal} onSubmit={onSubmitProductivityGain} />
        </div>
    </Chat.ContextProvider>;
};

export default ChatWindow;

